import React, { useEffect, useState } from "react"; // Added useState
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetInvoiceQuery,
  useGetInvoiceItemQuery,
} from "../../api/billingApi";
import { useGetAssessmentFeeStatusQuery } from "../../../logbook/api/logbookApi";
import { Loader2, ShieldCheck, Activity, Search } from "lucide-react";

export default function PaymentProcessingPage() {
  const { type, id } = useParams<{
    type: "invoice" | "item" | "logbook";
    id: string;
  }>();

  const navigate = useNavigate();

  // 1. Add a state to track if the minimum display time has passed
  const [canRedirect, setCanRedirect] = useState(false);
  // Poll attempts, so a failed logbook payment doesn't hang forever.
  const [attempts, setAttempts] = useState(0);

  // ✅ Fetch based on type
  const { data: invoice, isLoading: invoiceLoading } = useGetInvoiceQuery(id!, {
    skip: type !== "invoice",
    pollingInterval: 3000,
  });

  const { data: item, isLoading: itemLoading } = useGetInvoiceItemQuery(id!, {
    skip: type !== "item",
    pollingInterval: 3000,
  });

  const { data: assessment } = useGetAssessmentFeeStatusQuery(
    { logbookId: id! },
    { skip: type !== "logbook", pollingInterval: 3000 },
  );

  // Count poll ticks while waiting on a logbook assessment payment.
  useEffect(() => {
    if (type !== "logbook") return;
    setAttempts((a) => a + 1);
  }, [assessment, type]);

  /*
  |--------------------------------------------------------------------------
  | INITIAL DELAY TIMER
  |--------------------------------------------------------------------------
  | This ensures the user sees the processing screen for at least 3 seconds
  */
  useEffect(() => {
    const timer = setTimeout(() => {
      setCanRedirect(true);
    }, 3000); // 3 seconds delay

    return () => clearTimeout(timer);
  }, []);

  /*
  |--------------------------------------------------------------------------
  | PAYMENT STATUS WATCHER
  |--------------------------------------------------------------------------
  */
  useEffect(() => {
    // Only proceed if the 3-second delay is over
    if (!canRedirect) return;

    if (type === "invoice" && invoice) {
      console.log(invoice, "this is an invoicceeee");
      if (invoice.status === "paid" || invoice.status === "partial") {
        navigate(`/payment/success/${invoice.payment_reference}`);
      }
      // Note: Only redirect to failed if we are absolutely sure it's dead
      // or after a certain amount of polling.
      if (invoice.status === "failed") {
        navigate(`/payment/failed/${invoice.payment_reference}`);
      }

      if (invoice.status === "failed" || invoice.status === "unpaid") {
        navigate(`/payment/failed/${invoice.payment_reference}`);
      }
    }

    if (type === "item" && item) {
      console.log(item, "this is an itemmmm");
      if (item.balance_amount <= 0) {
        navigate(`/payment/success/${item.invoice_id}`);
      }
      if (item?.status === "failed" || item.status === "unpaid") {
        navigate(`/payment/failed/${item.id}`);
      }
    }

    if (type === "logbook" && assessment) {
      // Paid → back to the logbook, which now shows the "awaiting assessment" state.
      if (assessment.assessment_paid) {
        navigate(`/logbook/${id}`);
      }
      // Give OnePay ~30s (10 polls) to confirm; then bail back to the logbook,
      // where the candidate still sees the (unpaid) pay button to retry.
      else if (attempts > 10) {
        navigate(`/logbook/${id}`);
      }
    }
  }, [invoice, item, assessment, attempts, type, id, navigate, canRedirect]); // Added canRedirect to deps

  const displayRef =
    type === "invoice"
      ? invoice?.invoice_number
      : type === "logbook"
        ? "Logbook Assessment Fee"
        : item?.description || "Invoice Item";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full relative">
        {/* Glow Effects */}
        <div className="absolute -top-20 -left-20 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl" />

        <div className="bg-white border border-gray-100 rounded-[3rem] shadow-2xl shadow-gray-200 overflow-hidden relative z-10">
          {/* Progress Bar */}
          <div className="h-2 bg-gray-900 w-full relative overflow-hidden">
            <div className="absolute inset-0 bg-indigo-500 animate-progress-slide" />
          </div>

          <div className="p-10 md:p-14 text-center space-y-8">
            {/* Icon */}
            <div className="relative mx-auto w-24 h-24">
              <div className="absolute inset-0 rounded-[2.5rem] bg-indigo-50 animate-ping opacity-20" />
              <div className="relative z-10 w-full h-full rounded-[2.5rem] bg-white border border-gray-100 shadow-inner flex items-center justify-center">
                <Activity className="w-10 h-10 text-indigo-600" />
              </div>
            </div>

            {/* Header */}
            <div className="space-y-3">
              <h2 className="text-2xl font-black uppercase tracking-tighter text-gray-900">
                Verifying Payment
              </h2>

              <div className="flex items-center justify-center gap-2 text-[9px] font-black text-gray-400 uppercase tracking-[0.3em]">
                <Search className="w-3 h-3" />
                Syncing with OnePay Node
              </div>
            </div>

            {/* Audit Block */}
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 text-left space-y-3">
              <div className="flex justify-between items-center opacity-40">
                <span className="text-[8px] font-black uppercase">
                  Request Sent
                </span>
                <span className="text-[8px] font-mono">OK</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-[8px] font-black uppercase text-indigo-600">
                  Awaiting Confirmation
                </span>
                <Loader2 className="w-3 h-3 animate-spin text-indigo-600" />
              </div>

              <div className="pt-3 border-t border-gray-200/50 flex justify-between items-center italic">
                <span className="text-[8px] font-black text-gray-400 uppercase">
                  {type === "invoice"
                    ? "Invoice Ref"
                    : type === "logbook"
                      ? "Logbook"
                      : "Item"}
                </span>

                <span className="text-[9px] font-bold text-gray-900">
                  {displayRef || "..."}
                </span>
              </div>
            </div>

            {/* Footer Note */}
            <div className="pt-4">
              <p className="text-[10px] font-bold text-gray-400 leading-relaxed uppercase tracking-widest">
                Please do not refresh this page. <br />
                The transaction is being finalized.
              </p>
            </div>
          </div>

          {/* Bottom Security */}
          <div className="bg-gray-900 py-4 flex items-center justify-center gap-3">
            <ShieldCheck className="w-4 h-4 text-indigo-400" />
            <span className="text-[9px] font-black text-white uppercase tracking-[0.2em]">
              NIQS Encrypted Gateway
            </span>
          </div>
        </div>

        <p className="mt-8 text-center text-[9px] font-black text-gray-300 uppercase tracking-[0.5em]">
          Session ID: {id?.slice(0, 8)}...
        </p>
      </div>

      <style>{`
        @keyframes progress-slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-progress-slide {
          animation: progress-slide 2s infinite linear;
          width: 50%;
        }
      `}</style>
    </div>
  );
}
